import React from 'react';
import { 
  Shield, Lock, Eye, FileText, Server, UserCheck, 
  ArrowLeft, Database, Globe, Activity, Scale, 
  Info, ShieldCheck, CheckCircle2, ChevronRight, CreditCard, Wallet,
  HardDrive, LockKeyhole, Landmark, Cloud, ShieldAlert
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LegalCompliancePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const techStats = [
    { 
        icon: <div className="flex items-center gap-2"><Globe size={18}/><span className="text-[18px]">🇨🇭</span></div>, 
        label: t('legal.stats.residency'), 
        val: t('legal.stats_vals.residency') 
    },
    { 
        icon: <Cloud size={18}/>, 
        label: t('legal.stats.storage'), 
        val: "AWS S3 Enterprise" 
    },
    { 
        icon: <LockKeyhole size={18}/>, 
        label: t('legal.stats.encryption'), 
        val: "AES-256-GCM" 
    },
    { 
        icon: <Landmark size={18}/>, 
        label: t('legal.stats.gdpr'), 
        val: t('legal.stats_vals.gdpr') 
    },
    { 
        icon: <Database size={18}/>, 
        label: t('legal.stats.db'), 
        val: "PostgreSQL (RLS)" 
    },
    { 
        icon: <ShieldCheck size={18}/>, 
        label: t('legal.stats.security'), 
        val: "Zero-Trust Architecture" 
    },
    { 
        icon: <Activity size={18}/>, 
        label: t('legal.stats.monitoring'), 
        val: "Audit Log Live" 
    },
    { 
        icon: <UserCheck size={18}/>, 
        label: t('legal.stats.identity'), 
        val: "BCRYPT Hashing" 
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-24 pb-20 px-4 font-sans selection:bg-primary/20">
      <div className="max-w-5xl mx-auto space-y-12">
        
        {/* NAVIGAZIONE */}
        <div className="relative z-50"> 
          <button 
            onClick={() => navigate(-1)} 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-all mb-8 group cursor-pointer bg-transparent border-none p-0"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            {t('legal.back')}
          </button>
        </div>

        {/* HEADER SEKCIJA */}
        <div className="text-center space-y-6 pb-6">
          <div className="inline-flex items-center justify-center">
            <div className="relative flex items-center justify-center w-28 h-28 bg-primary/10 text-primary rounded-[2.5rem] rotate-0 hover:rotate-3 transition-transform duration-500 shadow-inner border border-primary/20">
              <ShieldCheck size={64} strokeWidth={1.2} />
              <span className="absolute inset-0 flex items-center justify-center font-black text-3xl select-none" style={{ transform: 'translateY(-5%)' }}>
                O
              </span>
            </div>
          </div>
          <div className="space-y-2">
         <h1 className="text-5xl md:text-5xl font-black uppercase tracking-tighter text-foreground italic">
  {t('legal.title_part1')} <span className="text-primary">{t('legal.title_part2')}</span>
</h1>
            <p className="text-muted-foreground text-[9px] md:text-xs uppercase tracking-[0.5em] font-black">
              {t('legal.header_subtitle')}
            </p>
          </div>
        </div>

        {/* BRZI INFO PANEL */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {techStats.map((item, i) => (
            <div key={i} className="bg-card/40 backdrop-blur-md border border-border p-5 rounded-[2.5rem] text-center hover:border-primary/50 transition-all duration-300 group hover:shadow-lg hover:shadow-primary/5">
              <div className="text-primary flex justify-center mb-2 group-hover:scale-110 transition-transform duration-300">{item.icon}</div>
              <p className="text-[8px] uppercase font-black text-muted-foreground tracking-[0.2em] mb-1">{item.label}</p>
              <p className="text-[11px] font-black text-foreground">{item.val}</p>
            </div>
          ))}
        </div>

        

   {/* 01. TERMINI DI UTILIZZO & INTEGRIDÀ */}
<section className="bg-card p-8 md:p-14 rounded-[3.5rem] border border-border shadow-sm space-y-10 relative overflow-hidden">
  
  <div className="absolute top-0 right-0 p-12 opacity-[0.02] rotate-12">
    <FileText size={250} />
  </div>
  
  <div className="flex items-center gap-6 border-b border-border pb-8">
    <span className="text-7xl font-black text-primary/5 select-none">01</span>
    <div>
      <h2 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
        <Lock size={28} className="text-primary" /> {t('legal.section01.title')}
      </h2>
      <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest italic tracking-tighter">
        {t('legal.section01.subtitle')}
      </p>
    </div>
  </div>
  
  <div className="text-muted-foreground text-[15px] leading-relaxed space-y-8">
    <div className="space-y-4">
      <p className="first-letter:text-5xl first-letter:font-black first-letter:text-primary first-letter:mr-2">
        {t('legal.section01.p1_part1')} <strong>Zero-Trust</strong>.
      </p>
      <p>
        {t('legal.section01.p2_part1')} <strong>{t('legal.section01.p2_bold')}</strong> {t('legal.section01.p2_part2')}
      </p>
      <p>
        {t('legal.section01.p3')}
      </p>
    </div>

    {/* SVJETLIJI PLAVI BLOK */}
    <div className="bg-primary/[0.08] p-8 md:p-10 rounded-[2.5rem] border border-primary/20 shadow-inner">
      <h3 className="text-lg font-black uppercase mb-4 flex items-center gap-3 text-foreground">
        <Shield size={22} className="text-primary" /> {t('legal.section01.integrity_title')}
      </h3>
      <p className="text-sm leading-relaxed text-foreground/80 mb-6 italic">
        <strong>{t('legal.section01.integrity_bold')}</strong> {t('legal.section01.integrity_desc')}
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="flex gap-4">
          <CheckCircle2 size={20} className="text-primary shrink-0" />
          <div>
            <p className="text-xs font-black text-foreground uppercase italic">{t('legal.section01.feature1_title')}</p>
            <p className="text-[11px] opacity-70">{t('legal.section01.feature1_desc')}</p>
          </div>
        </div>
        <div className="flex gap-4">
          <CheckCircle2 size={20} className="text-primary shrink-0" />
          <div>
            <p className="text-xs font-black text-foreground uppercase italic">{t('legal.section01.feature2_title')}</p>
            <p className="text-[11px] opacity-70">{t('legal.section01.feature2_desc')}</p>
          </div>
        </div>
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-8 pt-4">
      <div className="space-y-4 bg-muted/20 p-8 rounded-[2.5rem] border border-border/50">
        <h4 className="text-sm font-black uppercase text-foreground flex items-center gap-2">
          <UserCheck size={18} className="text-primary" /> {t('legal.section01.user_resp_title')}
        </h4>
        <p className="text-xs leading-relaxed">{t('legal.section01.user_resp_desc')}</p>
      </div>
      <div className="space-y-4 bg-muted/20 p-8 rounded-[2.5rem] border border-border/50">
        <h4 className="text-sm font-black uppercase text-foreground flex items-center gap-2">
          <Activity size={18} className="text-primary" /> {t('legal.section01.monitor_title')}
        </h4>
        <p className="text-xs leading-relaxed">{t('legal.section01.monitor_desc')}</p>
      </div>
    </div>
  </div>
</section>

 {/* 02. PRIVACY POLICY (GDPR) */}
<section className="bg-card p-8 md:p-14 rounded-[3.5rem] border border-border shadow-sm space-y-10">
  <div className="flex items-center gap-6 border-b border-border pb-8">
    <span className="text-7xl font-black text-primary/5 select-none">02</span>
    <div>
      <h2 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
        <Eye size={28} className="text-primary" /> {t('legal.section02.title')}
      </h2>
      <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest italic">
        {t('legal.section02.subtitle')}
      </p>
    </div>
  </div>

  <div className="space-y-8 text-[15px] text-muted-foreground leading-relaxed">
    <div className="space-y-4">
      <p>{t('legal.section02.gdpr_text1')}</p>
      <p>{t('legal.section02.gdpr_text2')}</p>
    </div>

    <div className="grid md:grid-cols-3 gap-6">
      {[
        { title: t('legal.section02.cards.purpose_t'), desc: t('legal.section02.cards.purpose_d') },
        { title: t('legal.section02.cards.retention_t'), desc: t('legal.section02.cards.retention_d') },
        { title: t('legal.section02.cards.security_t'), desc: t('legal.section02.cards.security_d') }
      ].map((card, i) => (
        <div key={i} className="p-6 border border-border rounded-[2rem] space-y-2 bg-background/50">
          <h5 className="font-black text-[10px] uppercase text-primary tracking-widest">{card.title}</h5>
          <p className="text-[12px] font-medium leading-relaxed">{card.desc}</p>
        </div>
      ))}
    </div>

    {/* NOVA SEKCIJA: AUDIT LOG OBJAŠNJENJE */}
    <div className="bg-primary/[0.05] p-8 md:p-10 rounded-[2.5rem] border border-primary/20 space-y-4">
      <h4 className="text-sm font-black uppercase text-foreground flex items-center gap-2 italic">
        <Activity size={18} className="text-primary" /> {t('legal.section02.audit.header')}
      </h4>
      <div className="grid md:grid-cols-2 gap-8">
        <p className="text-[13px] leading-relaxed italic text-muted-foreground">
          {t('legal.section02.audit.p1_part1')} <strong>Audit Log</strong> {t('legal.section02.audit.p1_part2')}
        </p>
        <p className="text-[13px] leading-relaxed italic text-muted-foreground">
          {t('legal.section02.audit.p2_part1')} <strong>{t('legal.section02.audit.p2_bold')}</strong> {t('legal.section02.audit.p2_part2')}
        </p>
      </div>
      <div className="flex flex-wrap gap-3 pt-2">
        <span className="text-[9px] font-black bg-background px-3 py-1 rounded-full border border-border uppercase">{t('legal.section02.audit.tag1')}</span>
        <span className="text-[9px] font-black bg-background px-3 py-1 rounded-full border border-border uppercase">{t('legal.section02.audit.tag2')}</span>
        <span className="text-[9px] font-black bg-background px-3 py-1 rounded-full border border-border uppercase">{t('legal.section02.audit.tag3')}</span>
      </div>
    </div>

    <div className="p-8 bg-muted/10 rounded-[2.5rem] border border-border italic text-xs space-y-3">
       <p className="font-black uppercase text-foreground">{t('legal.section02.cookies.title')}</p>
       <p>{t('legal.section02.cookies.desc')}</p>
    </div>
  </div>
</section>

      {/* 03. INFRASTRUTTURA AWS S3 E SVIZZERA */}
<section className="bg-card p-8 md:p-14 rounded-[3.5rem] border border-border shadow-sm space-y-10 bg-gradient-to-br from-card to-muted/20">
  <div className="flex items-center gap-6 border-b border-border pb-8">
    <span className="text-7xl font-black text-primary/5 select-none">03</span>
    <div>
      <h2 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
        <Server size={28} className="text-primary" /> {t('legal.section03.title')}
      </h2>
      <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest italic tracking-tighter">
        {t('legal.section03.subtitle')}
      </p>
    </div>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
    <div className="space-y-8">
      <div className="space-y-4">
        <h4 className="text-sm font-black uppercase text-foreground flex items-center gap-2">
          <Cloud size={20} className="text-primary" /> {t('legal.section03.aws_title')}
        </h4>
        <p className="text-xs leading-relaxed text-muted-foreground italic">
          {t('legal.section03.aws_desc_part1')} <strong>Amazon Web Services (AWS) S3 Storage</strong>. {t('legal.section03.aws_desc_part2')}
        </p>
      </div>

      <div className="space-y-4">
        <h4 className="text-sm font-black uppercase text-foreground flex items-center gap-2">
          <Globe size={20} className="text-primary" /> {t('legal.section03.swiss_title')}
        </h4>
        <p className="text-xs leading-relaxed text-muted-foreground italic">
          {t('legal.section03.swiss_desc_part1')} <strong>{t('legal.section03.swiss_bold')}</strong> {t('legal.section03.swiss_desc_part2')}
        </p>
      </div>

      <div className="p-6 bg-primary/5 border border-primary/10 rounded-[2rem]">
        <h4 className="text-[10px] font-black uppercase text-primary mb-2">{t('legal.section03.backup_title')}</h4>
        <p className="text-[11px] leading-relaxed italic">
          {t('legal.section03.backup_desc_part1')} <strong>Point-in-Time Recovery (PITR)</strong> {t('legal.section03.backup_desc_part2')} <strong>AES-256</strong> {t('legal.section03.backup_desc_part3')}
        </p>
      </div>

      <div className="p-6 bg-primary/5 border border-primary/10 rounded-[2rem]">
        <h4 className="text-[10px] font-black uppercase text-primary mb-2">{t('legal.section03.rls_title')}</h4>
        <p className="text-[11px] leading-relaxed italic">
          {t('legal.section03.rls_desc_part1')} <strong>Row-Level Security</strong>. {t('legal.section03.rls_desc_part2')}
        </p>
      </div>
    </div>

    {/* CERTIFICATION CARD */}
    <div className="bg-[#0f172a] text-slate-200 p-10 rounded-[3rem] space-y-6 shadow-2xl relative group overflow-hidden border border-slate-800">
      <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:scale-110 transition-transform">
        <ShieldCheck size={120} />
      </div>
      
      <div className="flex justify-between items-start">
        <h5 className="text-[10px] font-black uppercase tracking-[0.4em] flex items-center gap-2 text-primary">
          {t('legal.section03.cert_header')}
        </h5>
        <div className="flex gap-3 relative z-10">
          <a href="https://aws.amazon.com/it/compliance/cispe/" target="_blank" rel="noopener noreferrer" className="text-[9px] font-black bg-primary/10 text-primary border border-primary/20 px-2 py-1 rounded-md hover:bg-primary hover:text-white transition-all">
            AWS CISPE 🇨🇭
          </a>
          <a href="https://supabase.com/security" target="_blank" rel="noopener noreferrer" className="text-[9px] font-black bg-primary/10 text-primary border border-primary/20 px-2 py-1 rounded-md hover:bg-primary hover:text-white transition-all">
            SUPABASE SOC2
          </a>
        </div>
      </div>

      <div className="space-y-5">
        {[
          { icon: <HardDrive size={24} />, title: "ISO 27001 / 27017 / 27018", desc: t('legal.section03.cert_iso') },
          { icon: <Lock size={24} />, title: "SOC 1/2/3 Compliance", desc: t('legal.section03.cert_soc') },
          { icon: <ShieldCheck size={24} />, title: "GDPR Data Privacy Shield", desc: t('legal.section03.cert_gdpr') },
          { icon: <Globe size={24} />, title: "CSA STAR Certification", desc: t('legal.section03.cert_csa') },
          { icon: <ShieldAlert size={24} />, title: "Disaster Recovery", desc: t('legal.section03.cert_dr') }
        ].map((cert, idx) => (
          <div key={idx} className="flex items-center gap-4 border-b border-slate-800 pb-4 last:border-0 last:pb-0">
            <div className="text-primary">{cert.icon}</div>
            <div>
              <p className="text-xs font-bold uppercase tracking-tight">{cert.title}</p>
              <p className="text-[10px] opacity-60 italic">{cert.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-6 border-t border-slate-800">
        <p className="text-[11px] leading-relaxed opacity-80 italic">
          "{t('legal.section03.quote')}"
        </p>
        <p className="text-[9px] mt-2 opacity-40 uppercase tracking-tighter">
          {t('legal.section03.click_hint')}
        </p>
      </div>
    </div>
  </div>
</section>
{/* 04. DIRITTI DELL'INTERESSATO */}
        <section className="bg-card p-8 md:p-14 rounded-[3.5rem] border border-border shadow-sm space-y-10">
          <div className="flex items-center gap-6 border-b border-border pb-8">
            <span className="text-7xl font-black text-primary/5 select-none">04</span>
            <div>
              <h2 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Scale size={28} className="text-primary" /> {t('legal.section04.title')}
              </h2>
              <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest italic">
                {t('legal.section04.subtitle')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { t_key: "access_t", d_key: "access_d" },
              { t_key: "forget_t", d_key: "forget_d" },
              { t_key: "port_t", d_key: "port_d" },
              { t_key: "limit_t", d_key: "limit_d" }
            ].map((item, idx) => (
              <div key={idx} className="p-6 border border-border rounded-[2rem] hover:bg-primary/5 transition-all cursor-default group hover:border-primary/30">
                <h6 className="text-[11px] font-black uppercase text-primary mb-3 group-hover:translate-x-1 transition-transform italic tracking-tighter">
                  {t(`legal.section04.rights.${item.t_key}`)}
                </h6>
                <p className="text-[12px] text-muted-foreground font-medium leading-snug">
                  {t(`legal.section04.rights.${item.d_key}`)}
                </p>
              </div>
            ))}
          </div>
          
          <div className="bg-muted/30 p-8 rounded-[2.5rem] border border-border">
              <p className="text-[13px] leading-relaxed text-muted-foreground">
                {t('legal.section04.exercise_text')}
              </p>
          </div>
        </section>

       

      {/* 05. SICUREZZA DEI PAGAMENTI */}
        <section className="bg-card p-8 md:p-14 rounded-[3.5rem] border border-border shadow-sm space-y-10">
          <div className="flex items-center gap-6 border-b border-border pb-8">
            <span className="text-7xl font-black text-primary/5 select-none">05</span>
            <div>
              <h2 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Wallet size={28} className="text-primary" /> {t('legal.section05.title')}
              </h2>
              <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest italic">
                {t('legal.section05.subtitle')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { t_key: "stripe_t", d_key: "stripe_d" },
              { t_key: "pci_t", d_key: "pci_d" },
              { t_key: "crypto_t", d_key: "crypto_d" },
              { t_key: "methods_t", d_key: "methods_d" }
            ].map((item, idx) => (
              <div key={idx} className="p-6 border border-border rounded-[2rem] hover:bg-primary/5 transition-all cursor-default group hover:border-primary/30">
                <h6 className="text-[11px] font-black uppercase text-primary mb-3 group-hover:translate-x-1 transition-transform italic tracking-tighter">
                  {t(`legal.section05.points.${item.t_key}`)}
                </h6>
                <p className="text-[12px] text-muted-foreground font-medium leading-snug">
                  {t(`legal.section05.points.${item.d_key}`)}
                </p>
              </div>
            ))}
          </div>
          
          <div className="bg-muted/30 p-8 rounded-[2.5rem] border border-border">
              <p className="text-[13px] leading-relaxed text-muted-foreground italic">
                {t('legal.section05.payment_text')}
              </p>
          </div>
        </section>

        {/* 06. POVRAT I ODUSTAJANJE / RIMBORSI E RECESSO */}
{/* Dodaj ovaj blok ODMAH NAKON zaključne </section> od sekcije 05, PRIJE footer diva */}

<section className="bg-card p-8 md:p-14 rounded-[3.5rem] border border-border shadow-sm space-y-10">
  <div className="flex items-center gap-6 border-b border-border pb-8">
    <span className="text-7xl font-black text-primary/5 select-none">06</span>
    <div>
      <h2 className="text-3xl font-black uppercase tracking-tight flex items-center gap-3">
        <CreditCard size={28} className="text-primary" /> {t('legal.section06.title')}
      </h2>
      <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest italic">
        {t('legal.section06.subtitle')}
      </p>
    </div>
  </div>

  <div className="space-y-8 text-[15px] text-muted-foreground leading-relaxed">

    {/* INTRO */}
    <p className="first-letter:text-5xl first-letter:font-black first-letter:text-primary first-letter:mr-2">
      {t('legal.section06.intro')}
    </p>

    {/* 4 KARTICE — isti pattern kao section04 i section05 */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[
        { t_key: "period_t", d_key: "period_d" },
        { t_key: "how_t", d_key: "how_d" },
        { t_key: "conditions_t", d_key: "conditions_d" },
        { t_key: "timeline_t", d_key: "timeline_d" }
      ].map((item, idx) => (
        <div key={idx} className="p-6 border border-border rounded-[2rem] hover:bg-primary/5 transition-all cursor-default group hover:border-primary/30">
          <h6 className="text-[11px] font-black uppercase text-primary mb-3 group-hover:translate-x-1 transition-transform tracking-tighter">
            {t(`legal.section06.points.${item.t_key}`)}
          </h6>
          <p className="text-[12px] text-muted-foreground font-medium leading-snug">
            {t(`legal.section06.points.${item.d_key}`)}
          </p>
        </div>
      ))}
    </div>

    {/* IZUZECI — tamni warning blok */}
    <div className="bg-amber-500/[0.06] p-8 md:p-10 rounded-[2.5rem] border border-amber-500/20 space-y-4">
      <h4 className="text-sm font-black uppercase text-foreground flex items-center gap-2 italic">
        <Info size={18} className="text-amber-500" /> {t('legal.section06.exceptions_title')}
      </h4>
      <p className="text-[13px] leading-relaxed italic text-muted-foreground">
        {t('legal.section06.exceptions_desc')}
      </p>
    </div>

    {/* KONTAKT BLOK */}
    <div className="bg-muted/30 p-8 rounded-[2.5rem] border border-border">
      <p className="text-[13px] leading-relaxed text-muted-foreground italic">
        {t('legal.section06.contact_block')}
      </p>
    </div>

    {/* EU NOTA */}
    <div className="flex items-start gap-3 p-6 bg-primary/[0.04] rounded-[2rem] border border-primary/10">
      <Scale size={18} className="text-primary shrink-0 mt-0.5" />
      <p className="text-[12px] leading-relaxed text-muted-foreground font-medium">
        {t('legal.section06.eu_note')}
      </p>
    </div>

  </div>
</section>


      </div>

      


      {/* FOOTER - CONTATTI E NOTE LEGALI */}
      <div className="max-w-5xl mx-auto pt-20 border-t border-border space-y-12">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                  <div className="w-1.5 h-6 bg-primary rounded-full" />
                  <p className="text-[11px] text-muted-foreground uppercase tracking-[0.3em] font-black">
                    {t('legal.footer.contact_label')}
                  </p>
              </div>
              <div className="space-y-1">
                  <a href="mailto:info@orizon-aix.com" className="text-3xl md:text-3xl font-black text-foreground hover:text-primary transition-all tracking-tighter italic">
                      info@orizon-aix.com
                  </a>
                  <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
                    {t('legal.footer.locations')}
                  </p>
              </div>
          </div>
          
          <div className="bg-muted/30 p-10 rounded-[3rem] border border-border text-center md:text-left relative">
              <div className="absolute -top-3 -left-3 bg-primary text-primary-foreground text-[10px] font-black px-3 py-1 rounded-full uppercase italic">
                {t('legal.footer.update_tag')}
              </div>
              <p className="text-[12px] text-muted-foreground italic leading-relaxed">
                {t('legal.footer.disclaimer')}
              </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-6 opacity-40 grayscale hover:grayscale-0 transition-all duration-700 pb-10">
          <p className="text-[10px] font-black uppercase tracking-[0.3em]">
              © 2026 Orizon DESK. {t('legal.footer.secured_switzerland')}
          </p>
          <div className="flex gap-4">
              <div className="text-[9px] font-black border border-foreground px-4 py-1.5 rounded-full italic uppercase">GDPR Compliance</div>
              <div className="text-[9px] font-black border border-foreground px-4 py-1.5 rounded-full italic uppercase">AWS S3 Cloud</div>
              <div className="text-[9px] font-black border border-foreground px-4 py-1.5 rounded-full italic uppercase">Zurich Region</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalCompliancePage;